import 'leaflet/dist/leaflet.css';

import ChevronLeftRoundedIcon from '@mui/icons-material/ChevronLeftRounded';
import ChevronRightRoundedIcon from '@mui/icons-material/ChevronRightRounded';
import {
  Backdrop,
  Box, Button, CircularProgress, Dialog, IconButton,
  Snackbar,
  SnackbarContent,
  Stack, Typography, useTheme
} from '@mui/material';
import { GridApiPremium } from '@mui/x-data-grid-premium/models/gridApiPremium';
import { useSnackbar } from 'notistack';
import React, { FC, ForwardedRef, forwardRef, memo, useCallback, useContext, useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react';
import ReactGA from 'react-ga4';
import { MapContainer, TileLayer } from 'react-leaflet';
import { useNavigate } from 'react-router';
import { ConfirmDialog } from 'src/components/ConfirmDialog';
import { ReportPrintingDialogs, useReportPrinting } from 'src/components/ReportPrinting';
import { TRUCK_UNSET_LABEL, unsetColorFor } from 'src/constants/assignmentLabels';
import { actionBarHeight, appBarHeight } from 'src/constants/layout';
import { SCREEN_NAMES } from 'src/constants/screenNames';
import LicenseContext from 'src/contexts/LicenseContext';
import PlansContext from 'src/contexts/PlansContext';
import datetimeDecorator from 'src/decorators/datetime.decorator';
import { AllocateHistoryEntity } from 'src/entities/AllocateHistory.entity';
import { AssignTruckEntity } from 'src/entities/AssignTruck.entity';
import { EditPlaceEntity } from 'src/entities/EditPlace.entity';
import { GarageEntity } from 'src/entities/Garage.entity';
import {
  PlanningMapUnallocatedOrderPositionEntity
} from 'src/entities/import/PlanningMapUnallocatedOrderPosition.entity';
import { PartnerCompanyEntity } from 'src/entities/PartnerCompanyEntity';
import { RespectType, Senario, WeekDayEn } from 'src/entities/planningEntity';
import { PlanningOrderStatisticsEntity } from 'src/entities/PlanningOrderStatistics.entity';
import { AsyncPlanningResponseEntity } from 'src/entities/planningResponseEntity';
import { PlanningsDeliveryEntity, PlanningsDeliveryWithTruckAndDriverEntity } from 'src/entities/PlanningsDelivery.entity';
import { PlanningsDriverEntity } from 'src/entities/PlanningsDriver.entity';
import { PlanningsGroupEntity } from 'src/entities/PlanningsGroup.entity';
import { PlanningsMapGaragePositionEntity } from 'src/entities/PlanningsMapGaragePosition.entity';
import { PlanningsMapOperationPositionEntity } from 'src/entities/PlanningsMapOperationPosition.entity';
import { PlanningsOperationEntity } from 'src/entities/PlanningsOperation.entity';
import {
  PlanningsOperationDeliveryByDeliveryIdEntity,
  PlanningsOperationEntitiesWithStatsByDeliveryIdEntity
} from 'src/entities/PlanningsOperationEntitiesWithStatsByDeliveryId.entity';
import { PlanningRunningEntity } from 'src/entities/PlanningsRunning.entity';
import { PlanningSuggestion } from 'src/entities/PlanningsSuggestions.entity';
import { PlanningsTruckEntity } from 'src/entities/PlanningsTruck.entity';
import { PositionEntity } from 'src/entities/PositionEntity';
import { SelectedCycle } from 'src/entities/SelectedCycle.entity';
import { ShipmentOrderLegRunForPlanEntity } from 'src/entities/ShipmentOrderLegRun.entity';
import { TransferRequestEntity } from 'src/entities/transferRequestEntity';
import { PlanningsOperationPlace } from 'src/models/PlanningsOperationGroup.model';
import arrayUtil from 'src/utils/array.util';
import { EstimationMethodVo } from 'src/vo/EstimationMethod.vo';
import { PlanningViewKind } from 'src/vo/PlanningViewKind.vo';
import { RelaxedRuleVo } from 'src/vo/RelaxedRule.vo';

import V2OrdersPresenter from '../V2OrdersPresenter';

import NotAllocSuggestionComponent from './components/NotAllocSuggestion.component';
import ActionsOnMapPresenter from './presenters/ActionsOnMapPresenter';
import ActionsPresenter from './presenters/ActionsPresenter';
import AllocateHistoryPresenter from './presenters/AllocateHistoryPresenter';
import EstimationDialogPresenter from './presenters/EstimationDialogPresenter';
import MapFunctionPresenter from './presenters/MapFunctionPresenter';
import OrderPolylinesPresenter from './presenters/OrderPolylinesPresenter';
import OrdersPresenter from './presenters/OrdersPresenter';
import PlanningMapCurrentPositionsPresenter from './presenters/PlanningMapCurrentPositions.presenter';
import PlanningMapDeliveriesPolylinesPresenter from './presenters/PlanningMapDeliveriesPolylines.presenter';
import PlanningMapGarageMarkersPresenter from './presenters/PlanningMapGarageMarkers.presenter';
import PlanningMapOperationMarkersPresenter from './presenters/PlanningMapOperationMarkers.presenter';
import PlanningMapOrderMarkersPresenter from './presenters/PlanningMapOrderMarkers.presenter';
import PlanningOrderPresenter from './presenters/PlanningOrder.presenter';
import PlanningPresenter from './presenters/PlanningPresenter';
import PlanningTrucksPresenter from './presenters/PlanningTrucks.presenter';
import { ScenarioPlanningListDialog } from './presenters/ScenarioPlanningListDialog';
import TariffCalculationDialog, { TariffCalculationParams } from './presenters/TariffCalculationDialog';

type Props = {
  startOn: string;
  endOn: string;
  orderIds: number[];
  selectedOrderIds: number[];
  addSelectedOrderId: (id: number) => void;
  removeSelectedOrderId: (id: number) => void;
  unit: string;
  isLoading: boolean;
  updateBalancedLoading: (balancedLoading: boolean) => void;
  updateIsRespectRecent: (bool: boolean) => void;
  updateExcludeToll: (bool: boolean) => void;
  mutateDeleteShiftsOperations: (deliveryIds: number[]) => void;
  mutateDeleteOrdersOperations: (requestOrderIds: number[]) => void;
  planningRequest: (shiftIds: number[], orderIds: number[]) => void;
  transferRequest: (transferToShiftIds: number[], requestOrderIds: number[]) => void;
  transfer: TransferRequestEntity;
  setTransfer: (entity: TransferRequestEntity) => void;
  resetTransfer: () => void;
  mutateDeleteOrder: () => void;
  mutateDeleteSpecificOrder: (orderId: number) => void;
  mutateCloneOrder: (shipimentOrderId: number) => void;
  mutateCloneLastWeekShifts: (date: string) => void;
  displayOrderId: number | undefined;
  resetDisplayOrderId: () => void;
  updateDisplayOrderId: (orderId: number) => void;
  planningMapUnallocatedOrderPositionEntities: PlanningMapUnallocatedOrderPositionEntity[];
  garageData: GarageEntity[];
  truckEntities: PlanningsTruckEntity[];
  driverEntities: PlanningsDriverEntity[];
  deliveryEntities: PlanningsDeliveryEntity[];
  operationEntities: PlanningsOperationEntity[];
  shipmentOrderLegRunEntityMap: Map<number, ShipmentOrderLegRunForPlanEntity>;
  swapRequest: (swapFromDeliveryId: number, swapToDeliveryId: number) => void;
  planningOrderStatisticsEntity: PlanningOrderStatisticsEntity | undefined;
  groupEntities: PlanningsGroupEntity[];
  planningsMapGaragePositionEntities: PlanningsMapGaragePositionEntity[];
  planningsMapOperationPositionEntities: PlanningsMapOperationPositionEntity[];
  planningsOperationEntitiesWithStatsByDeliveryIdEntity: PlanningsOperationEntitiesWithStatsByDeliveryIdEntity
  updateRespectType: (respectType: RespectType) => void;
  updateRespectDaysOfWeek: (days: WeekDayEn[]) => void;
  updateRespectOn: (on: string) => void;
  updateIncludeFutureDeliveries: (bool: boolean) => void;
  updateAutoSplitOrders: (bool: boolean) => void;
  updateDontExchangePreroute: (bool: boolean) => void;
  updateConcurrentAllOrNothing: (bool: boolean) => void;
  updateMLPlanning: (bool: boolean) => void;
  updateIsTimeTableBackward: (bool: boolean) => void;
  sendOperationMail: () => void;
  refreshOperationStatuses: () => void;
  lastOperationStatusUpdated: string;
  resetDrivers: () => void;
  flyToPosition: PositionEntity | undefined;
  resetFlyToPosition: () => void;
  fitBoundsPositions: PositionEntity[];
  resetFitBoundsPositions: () => void;
  currentHistoryVersion: string | undefined;
  setCurrentHistoryVersion: (version: string) => void;
  resetCurrentHistoryVersion: () => void;
  resetAllocateHistories: () => void;
  rollbackRequest: () => void;
  customInputFields: string[];
  relaxedRules: RelaxedRuleVo[];
  selectedRelaxedRules: RelaxedRuleVo[];
  setSelectedRelaxedRules: (value: RelaxedRuleVo[]) => void;
  setPriorityLargeTrucks: (priority: number) => void;
  priorityLargeTrucks: number;
  updateExpandWorkingStartMinutes: (minutes: number) => void;
  updateExpandWorkingEndMinutes: (minutes: number) => void;
  updateExpandLoadStartMinutes: (minutes: number) => void;
  updateExpandLoadEndMinutes: (minutes: number) => void;
  updateExpandUnloadStartMinutes: (minutes: number) => void;
  updateExpandUnloadEndMinutes: (minutes: number) => void;
  updateExpandMaxVolumeRate: (rate: number) => void;
  updateExpandMaxLoadCapacityRate: (rate: number) => void;
  firstVisitSet: Set<string>;
  latestAllocateHistory: AllocateHistoryEntity | undefined;
  planningRunningEntities: PlanningRunningEntity[];
  refreshRunningEntities: () => void;
  cancelRunningPlan: (id: number) => void;
  updateMLSourceType: (mlSourceTypes: number) => void;
  requestSingleAlgorithmPlanning: (deliveryId: number, orderOperationIdsForSort: number[], deleteOrderIdsFromOperations: number[], orderOperationCycleIndexes?: { [key: number]: number }) => void;
  selectedCycleIndexes: SelectedCycle[];
  updateSelectedCycleIndexes: (deliveryId: number, cycleIndexes: number[]) => void;
  planningsOperationDeliveryByDeliveryIdEntity: PlanningsOperationDeliveryByDeliveryIdEntity;
  editPlaces: EditPlaceEntity[];
  resetEditPlaces: () => void;
  updateEditPlaces: (deliveryId: number, cycleIndex: number, places: PlanningsOperationPlace[]) => void;
  addEmptyCycle: (deliveryId: number) => void;
  removeEmptyCycle: (deliveryId: number, cycleIndex: number) => void;
  requestScenarioPlanning: (scenarioId: number) => void;
  asyncPlanningResponse: AsyncPlanningResponseEntity;
  mutateRestoreSplittedOrder: (orderId: number) => void;
  mutateRestoreSplittedOrders: () => void;
  updateOptimizeForLoad: (bool: boolean) => void;
  updateForceNullBase: (bool: boolean) => void;
  updateTrunkTransportation: (bool: boolean) => void;
  currentView: PlanningViewKind;
  updateView: (val: PlanningViewKind) => void;
  requestForcePlanning: (delivery: PlanningsDeliveryWithTruckAndDriverEntity, orderIds: number[]) => void;
  resetQuery: () => void;
  forcePlanningQueue: { delivery: PlanningsDeliveryWithTruckAndDriverEntity, orderIds: number[], latestAlgorithmRequestVersion: number }[];
  currentForcePlanning: { delivery: PlanningsDeliveryWithTruckAndDriverEntity, orderIds: number[], latestAlgorithmRequestVersion: number };
  assignTruckToTemporaryDeliveryRequest: (entity: AssignTruckEntity) => void;
  assignTruckQueue: AssignTruckEntity[];
  currentAssigning: AssignTruckEntity;
  allDeliveriesLoaded: boolean;
  partnerCompanyEntities: PartnerCompanyEntity[];
  ref?: ForwardedRef<{ afterResetQuery:() => void; } | null>;
  deleteTemporaryDelivery: (deliveryId: number) => void;
  afterResetOrders: () => void;
  notAllocSuggestions: PlanningSuggestion[];
  handleConfirmDeliveries: (deliveryIds: number[]) => void;
  handleUnconfirmDeliveries: (deliveryIds: number[]) => void;
  isConfirming: boolean;
  isUnconfirming: boolean;
  updateActivateIncrementalMaxNumPistons: (bool: boolean) => void;
  updateSenario: (senario: Senario) => void;
  updateDisableGlobalOpt: (bool: boolean) => void;
  updateTraceCompanyId: (id: number) => void;
  showLegRunLoading: boolean;
  handleTariffCalculationExecute: (params: TariffCalculationParams, onSuccess?: () => void) => void;
  tariffCalculationIsExecuting: boolean;
}

const Presenter: FC<Props> = memo(
  forwardRef(({
    startOn,
    endOn,
    orderIds,
    unit,
    isLoading,
    selectedOrderIds,
    addSelectedOrderId,
    removeSelectedOrderId,
    updateRespectType,
    updateRespectDaysOfWeek,
    updateRespectOn,
    updateBalancedLoading,
    updateIsRespectRecent,
    updateExcludeToll,
    updateIncludeFutureDeliveries,
    updateAutoSplitOrders,
    updateDontExchangePreroute,
    updateConcurrentAllOrNothing,
    updateMLPlanning,
    planningRequest,
    mutateDeleteShiftsOperations,
    mutateDeleteOrdersOperations,
    transferRequest,
    transfer,
    setTransfer,
    resetTransfer,
    mutateDeleteOrder,
    mutateDeleteSpecificOrder,
    mutateCloneOrder,
    mutateCloneLastWeekShifts,
    displayOrderId,
    resetDisplayOrderId,
    updateDisplayOrderId,
    planningMapUnallocatedOrderPositionEntities,
    garageData,
    truckEntities,
    driverEntities,
    deliveryEntities,
    operationEntities,
    shipmentOrderLegRunEntityMap,
    swapRequest,
    planningOrderStatisticsEntity,
    groupEntities,
    planningsMapGaragePositionEntities,
    planningsMapOperationPositionEntities,
    planningsOperationEntitiesWithStatsByDeliveryIdEntity,
    updateIsTimeTableBackward,
    sendOperationMail,
    refreshOperationStatuses,
    lastOperationStatusUpdated,
    resetDrivers,
    flyToPosition,
    resetFlyToPosition,
    fitBoundsPositions,
    resetFitBoundsPositions,
    currentHistoryVersion,
    setCurrentHistoryVersion,
    resetCurrentHistoryVersion,
    resetAllocateHistories,
    rollbackRequest,
    customInputFields,
    relaxedRules,
    selectedRelaxedRules,
    setSelectedRelaxedRules,
    setPriorityLargeTrucks,
    priorityLargeTrucks,
    updateExpandWorkingStartMinutes,
    updateExpandWorkingEndMinutes,
    updateExpandLoadStartMinutes,
    updateExpandLoadEndMinutes,
    updateExpandUnloadStartMinutes,
    updateExpandUnloadEndMinutes,
    updateExpandMaxVolumeRate,
    updateExpandMaxLoadCapacityRate,
    firstVisitSet,
    latestAllocateHistory,
    planningRunningEntities,
    refreshRunningEntities,
    cancelRunningPlan,
    updateMLSourceType,
    requestSingleAlgorithmPlanning,
    selectedCycleIndexes,
    updateSelectedCycleIndexes,
    planningsOperationDeliveryByDeliveryIdEntity,
    editPlaces,
    resetEditPlaces,
    updateEditPlaces,
    addEmptyCycle,
    removeEmptyCycle,
    requestScenarioPlanning,
    asyncPlanningResponse,
    mutateRestoreSplittedOrder,
    mutateRestoreSplittedOrders,
    updateOptimizeForLoad,
    updateForceNullBase,
    updateTrunkTransportation,
    currentView,
    updateView,
    requestForcePlanning,
    resetQuery,
    forcePlanningQueue,
    currentForcePlanning,
    assignTruckToTemporaryDeliveryRequest,
    assignTruckQueue,
    currentAssigning,
    allDeliveriesLoaded,
    partnerCompanyEntities,
    deleteTemporaryDelivery,
    afterResetOrders,
    notAllocSuggestions,
    handleConfirmDeliveries,
    handleUnconfirmDeliveries,
    isConfirming,
    isUnconfirming,
    updateActivateIncrementalMaxNumPistons,
    updateSenario,
    updateDisableGlobalOpt,
    updateTraceCompanyId,
    showLegRunLoading,
    handleTariffCalculationExecute,
    tariffCalculationIsExecuting,
  }, ref) => {
    const childRef = useRef<{ afterResetQuery:() => void; } | null>(null);

    useImperativeHandle(ref, () => ({
    afterResetQuery: () => {
      if (childRef.current) {
        childRef.current.afterResetQuery();
      }
    }
  }));

  const navigate = useNavigate();
  const theme = useTheme();
  const licenseContext = useContext(LicenseContext);
  const plansContext = useContext(PlansContext);
  const { calculating } = plansContext;
  const { closeSnackbar, enqueueSnackbar } = useSnackbar();

  const contentHeight = useMemo(() => `calc(100vh - ${appBarHeight + actionBarHeight}px)`, []);

  const selectedDeliveryIds = useMemo(() => selectedCycleIndexes.map((it) => it.deliveryId), [selectedCycleIndexes]);

  const { billingTotal, paymentTotal } = useMemo(() => {
    if (!deliveryEntities) return { billingTotal: 0, paymentTotal: 0 };

    const billingByLegRunId = new Map<number, number>();
    const paymentByLegRunId = new Map<number, number>();

    deliveryEntities.forEach((delivery) => {
      const entities = delivery?.legRunEntity?.shipmentOrderLegRuns;
      entities.forEach((entity) => {
        if (entity.shipment_order_billing_amount) {
          const { shipment_order_leg_run_id, total_amount } = entity.shipment_order_billing_amount;
          if (!billingByLegRunId.has(shipment_order_leg_run_id)) {
            billingByLegRunId.set(shipment_order_leg_run_id, total_amount);
          }
        }
        if (entity.shipment_order_leg_run_payment_amount) {
          const { shipment_order_leg_run_id, total_amount } = entity.shipment_order_leg_run_payment_amount;
          if (!paymentByLegRunId.has(shipment_order_leg_run_id)) {
            paymentByLegRunId.set(shipment_order_leg_run_id, total_amount);
          }
        }
      });
    });

    let billingTotal = 0;
    billingByLegRunId.forEach((amount) => { billingTotal += amount; });

    let paymentTotal = 0;
    paymentByLegRunId.forEach((amount) => { paymentTotal += amount; });

    return { billingTotal, paymentTotal };
  }, [deliveryEntities]);

  const deliveryDrawerWidthInitialState = 430 as const;
  const [deliveryDrawerWidth, setDeliveryDrawerWidth] = useState<number>(deliveryDrawerWidthInitialState);
  const [deliveryDrawerIsOpen, setDeliveryDrawerIsOpen] = useState<boolean>(true);
  const openDeliveryDrawer = useCallback(() => {
    setDeliveryDrawerIsOpen(true);
  }, []);
  const closeDeliveryDrawer = useCallback(() => {
    setDeliveryDrawerIsOpen(false);
  }, []);
  useEffect(() => {
    setDeliveryDrawerWidth(deliveryDrawerIsOpen ? deliveryDrawerWidthInitialState : 0);
  }, [deliveryDrawerIsOpen]);

  const [showNotAllocSuggestionSnackbar, setShowNotAllocSuggestionSnackbar] = useState(false);
  useEffect(() => {
    setShowNotAllocSuggestionSnackbar(notAllocSuggestions && notAllocSuggestions.length > 0);
  }, [notAllocSuggestions]);

  const [showTruckStatus, setShowTruckStatus] = useState(false);

  const unallocatedDrawerWidthInitialState = 300 as const;
  const [unallocatedDrawerWidth, setUnallocatedDrawerWidth] = useState<number>(unallocatedDrawerWidthInitialState);
  const [unallocatedDrawerIsOpen, setUnallocatedDrawerIsOpen] = useState<boolean>(true);
  const openUnallocatedDrawer = useCallback(() => {
    setUnallocatedDrawerIsOpen(true);
  }, []);
  const closeUnallocatedDrawer = useCallback(() => {
    setUnallocatedDrawerIsOpen(false);
  }, []);
  useEffect(() => {
    setUnallocatedDrawerWidth(unallocatedDrawerIsOpen ? unallocatedDrawerWidthInitialState : 0);
  }, [unallocatedDrawerIsOpen]);

  const [allocatedHistoriesDialogIsOpen, setAllocatedHistoriesDialogIsOpen] = useState<boolean>(false);
  const allocateHistoriesOnClick = useCallback(() => {
    ReactGA.event('click', { screen_name: SCREEN_NAMES.PLANNING, button_name: '元に戻す' });
    setAllocatedHistoriesDialogIsOpen(true);
  }, []);

  const [displayOrderDialogIsOpen, setDisplayOrderDialogIsOpen] = useState(false);
  useEffect(() => {
    setDisplayOrderDialogIsOpen(!!displayOrderId);
  }, [displayOrderId]);

  const [transferDialogIsOpen, setTransferDialogIsOpen] = useState<boolean>(false);
  const openTransferDialog = useCallback((entity: TransferRequestEntity) => {
    setTransfer(entity);
    setTransferDialogIsOpen(true);
  }, [setTransfer]);
  const transferDialogOnClose = useCallback(() => {
    resetTransfer();
    setTransferDialogIsOpen(false);
  }, [resetTransfer]);

  const [planningDialogIsOpen, setPlanningDialogIsOpen] = useState<boolean>(false);
  const planningButtonOnClick = useCallback(() => {
    ReactGA.event('click', { screen_name: SCREEN_NAMES.PLANNING, button_name: '自動配車' });
    const targets = [
      selectedDeliveryIds.length === 0 ? '車両' : '',
    ].filter((it) => it);
    if (targets.length > 0) {
      closeSnackbar();
      const message = `配車する${targets.join('・')}を選択してください。`;
      ReactGA.event('error', { screen_name: SCREEN_NAMES.PLANNING, button_name: '自動配車', label: message });
      enqueueSnackbar(message);
      return;
    }
    setPlanningDialogIsOpen(true);
  }, [closeSnackbar, enqueueSnackbar, selectedDeliveryIds]);

  const planningDialogOnClose = useCallback(() => {
    setPlanningDialogIsOpen(false);
  }, []);

  const customImportButtonOnClick = useCallback(() => {
    ReactGA.event('click', { screen_name: SCREEN_NAMES.PLANNING_ORDER_IMPORT, button_name: 'カスタムフォーマット' });
    void navigate('/import');
  }, []);

  const printTargetShiftIds = useMemo(() => {
    const selectedShiftIds = arrayUtil.uniq(
      deliveryEntities.filter((it) => selectedDeliveryIds.includes(it.id)).map((it) => it.shiftId),
    );
    const allShiftIds = arrayUtil.uniq(deliveryEntities.map((it) => it.shiftId));
    return selectedShiftIds.length ? selectedShiftIds : allShiftIds;
  }, [deliveryEntities, selectedDeliveryIds]);

  const reportPrinting = useReportPrinting({
    startOn,
    endOn,
    shiftIds: printTargetShiftIds,
    onAnalyticsEvent: (action, buttonName) => ReactGA.event(action, { screen_name: SCREEN_NAMES.PLANNING, button_name: buttonName }),
  });

  const [allocateHistoriesForCompany, setAllocateHistoriesForCompany] = useState<AllocateHistoryEntity[]>([]);
  useEffect(() => {
    const list = plansContext.allocateHistories.filter((it) => it.companyId === licenseContext.config?.selected_company_id);
    setAllocateHistoriesForCompany(list);
  }, [plansContext.allocateHistories, licenseContext.config?.selected_company_id]);

  const [estimationMethod, setEstimationMethod] = useState<EstimationMethodVo | undefined>(undefined);

  const estimationMethodOnClick = useCallback((value: EstimationMethodVo) => {
    ReactGA.event('click', { screen_name: SCREEN_NAMES.PLANNING, button_name: `見積もり - ${value}` });
    setEstimationMethod(value);
  }, []);

  const [estimationDialogIsOpen, setEstimationDialogIsOpen] = useState<boolean>(false);
  const estimationButtonOnClick = useCallback(() => {
    ReactGA.event('click', { screen_name: SCREEN_NAMES.PLANNING, button_name: '見積もり' });
    setEstimationDialogIsOpen(true);
  }, []);
  const estimationOnClose = useCallback(() => {
    ReactGA.event('close', { screen_name: SCREEN_NAMES.PLANNING, button_name: '見積もり' });
    setEstimationDialogIsOpen(false);
    setEstimationMethod(undefined);
  }, []);

  const allocateHistoryListItemButtonOnClick = useCallback((version: string) => {
    setCurrentHistoryVersion(version);
  }, [setCurrentHistoryVersion]);

  const allocateHistoryCancelButtonOnClick = useCallback(() => {
    ReactGA.event('click', { screen_name: SCREEN_NAMES.PLANNING, button_name: '元に戻す キャンセル' });
    resetCurrentHistoryVersion();
    setAllocatedHistoriesDialogIsOpen(false);
  }, [resetCurrentHistoryVersion]);

  const allocateHistoryButtonOnClick = useCallback(() => {
    ReactGA.event('apply', { screen_name: SCREEN_NAMES.PLANNING, button_name: '元に戻す' });
    rollbackRequest();
  }, [rollbackRequest]);

  const [windowWidth, setWindowWidth] = useState<number>(0);

  const [actionContainerWidth, setActionContainerWidth] = useState<number>(0);

  useEffect(() => {
    setActionContainerWidth(windowWidth - deliveryDrawerWidth - unallocatedDrawerWidth);
  }, [windowWidth, deliveryDrawerWidth, unallocatedDrawerWidth]);

  const ordersMemo = useMemo(() => (
    <OrdersPresenter
      ids={orderIds}
      selectedIds={selectedOrderIds}
      addSelectedId={addSelectedOrderId}
      removeSelectedId={removeSelectedOrderId}
      unit={unit}
      isLoading={isLoading}
      startOn={startOn}
      endOn={endOn}
      mutateDeleteOrder={mutateDeleteOrder}
      mutateDeleteSpecificOrder={mutateDeleteSpecificOrder}
      mutateCloneOrder={mutateCloneOrder}
      planningOrderStatisticsEntity={planningOrderStatisticsEntity}
      resetAllocateHistories={resetAllocateHistories}
      customInputFields={customInputFields}
      truckEntities={truckEntities}
      driverEntities={driverEntities}
      deliveryEntities={deliveryEntities}
      shipmentOrderLegRunEntityMap={shipmentOrderLegRunEntityMap}
      unallocatedDrawerWidth={unallocatedDrawerWidth}
      mutateRestoreSplittedOrder={mutateRestoreSplittedOrder}
      mutateRestoreSplittedOrders={mutateRestoreSplittedOrders}
      showLegRunLoading={showLegRunLoading}
    />
  ), [orderIds, selectedOrderIds, addSelectedOrderId, removeSelectedOrderId, unit, isLoading, startOn, endOn, mutateDeleteOrder, mutateDeleteSpecificOrder, mutateCloneOrder, planningOrderStatisticsEntity, resetAllocateHistories, customInputFields, truckEntities, driverEntities, deliveryEntities, shipmentOrderLegRunEntityMap, unallocatedDrawerWidth, mutateRestoreSplittedOrder, mutateRestoreSplittedOrders, showLegRunLoading]);

  const [scenarioPlanningDialogIsOpen, setScenarioPlanningDialogIsOpen] = useState(false);
  const [tariffCalculationDialogIsOpen, setTariffCalculationDialogIsOpen] = useState(false);

  const onTariffCalculationExecute = useCallback((params: TariffCalculationParams) => {
    handleTariffCalculationExecute(params, () => setTariffCalculationDialogIsOpen(false));
  }, [handleTariffCalculationExecute]);

  const tariffCalculationDialogMemo = useMemo(() => (
    <TariffCalculationDialog
      dialogIsOpen={tariffCalculationDialogIsOpen}
      setDialogIsOpen={setTariffCalculationDialogIsOpen}
      selectedDeliveryIds={selectedDeliveryIds}
      onExecute={onTariffCalculationExecute}
      isExecuting={tariffCalculationIsExecuting}
    />
  ), [tariffCalculationDialogIsOpen, selectedDeliveryIds, onTariffCalculationExecute, tariffCalculationIsExecuting]);

  const scenarioDialogMemo = useMemo(() => (
    <ScenarioPlanningListDialog
      dialogIsOpen={scenarioPlanningDialogIsOpen}
      setDialogIsOpen={setScenarioPlanningDialogIsOpen}
      customInputFields={customInputFields}
      requestScenarioPlanning={requestScenarioPlanning}
    />
  ), [customInputFields, scenarioPlanningDialogIsOpen, requestScenarioPlanning]);

  const [orderDataGridApiRef, setOrderGridApiRef] = useState<React.MutableRefObject<GridApiPremium>>();

  const selectedOrderIdsForPlanning = useMemo(() => {
    if (currentView === 'map') {
      return selectedOrderIds;
    }
    if (orderDataGridApiRef && orderDataGridApiRef.current) {
      const selected = Array.from(orderDataGridApiRef.current.getSelectedRows().keys()).map(Number);
      // 未割り当てのものだけに絞る
      return selected.filter((it) => orderIds.includes(it));
    }
    return [];
  // planningDialogIsOpen が変わるときに更新しないと古い値を使ってしまう。
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [planningDialogIsOpen, currentView, orderDataGridApiRef, orderIds, selectedOrderIds]);

  const planningMemo = useMemo(() => (
    <PlanningPresenter
      screenName={SCREEN_NAMES.PLANNING}
      planningRequest={planningRequest}
      updateBalancedLoading={updateBalancedLoading}
      updateExcludeToll={updateExcludeToll}
      onClose={planningDialogOnClose}
      selectedDeliveryIds={selectedDeliveryIds}
      selectedOrderIds={selectedOrderIdsForPlanning}
      startOn={startOn}
      updateIncludeFutureDeliveries={updateIncludeFutureDeliveries}
      updateAutoSplitOrders={updateAutoSplitOrders}
      updateDontExchangePreroute={updateDontExchangePreroute}
      updateConcurrentAllOrNothing={updateConcurrentAllOrNothing}
      relaxedRules={relaxedRules}
      selectedRelaxedRules={selectedRelaxedRules}
      setSelectedRelaxedRules={setSelectedRelaxedRules}
      setPriorityLargeTrucks={setPriorityLargeTrucks}
      priorityLargeTrucks={priorityLargeTrucks}
      updateExpandWorkingStartMinutes={updateExpandWorkingStartMinutes}
      updateExpandWorkingEndMinutes={updateExpandWorkingEndMinutes}
      updateExpandLoadStartMinutes={updateExpandLoadStartMinutes}
      updateExpandLoadEndMinutes={updateExpandLoadEndMinutes}
      updateExpandUnloadStartMinutes={updateExpandUnloadStartMinutes}
      updateExpandUnloadEndMinutes={updateExpandUnloadEndMinutes}
      updateExpandMaxVolumeRate={updateExpandMaxVolumeRate}
      updateExpandMaxLoadCapacityRate={updateExpandMaxLoadCapacityRate}
      updateActivateIncrementalMaxNumPistons={updateActivateIncrementalMaxNumPistons}
      updateSenario={updateSenario}
      updateDisableGlobalOpt={updateDisableGlobalOpt}
      updateTraceCompanyId={updateTraceCompanyId}
    />
  ), [planningRequest, updateExcludeToll, planningDialogOnClose, selectedDeliveryIds, selectedOrderIdsForPlanning, startOn, updateIncludeFutureDeliveries, updateAutoSplitOrders, relaxedRules, selectedRelaxedRules, setSelectedRelaxedRules, setPriorityLargeTrucks, priorityLargeTrucks, updateExpandLoadStartMinutes, updateExpandLoadEndMinutes, updateExpandUnloadStartMinutes, updateExpandUnloadEndMinutes, updateExpandMaxVolumeRate, updateExpandMaxLoadCapacityRate, updateDontExchangePreroute, updateConcurrentAllOrNothing, updateBalancedLoading, updateExpandWorkingStartMinutes, updateExpandWorkingEndMinutes, updateActivateIncrementalMaxNumPistons, updateSenario, updateDisableGlobalOpt, updateTraceCompanyId]);

  const transferMemo = useMemo(() => (
    <PlanningPresenter
      screenName={SCREEN_NAMES.PLANNING}
      planningRequest={transferRequest}
      updateBalancedLoading={updateBalancedLoading}
      updateExcludeToll={updateExcludeToll}
      onClose={transferDialogOnClose}
      selectedDeliveryIds={transfer && transfer.transferToDeliveryId && transfer.transferFromDeliveryIds ? [transfer.transferToDeliveryId, ...transfer.transferFromDeliveryIds] : []}
      selectedOrderIds={transfer && transfer.orderIds ? transfer.orderIds : []}
      startOn={startOn}
      updateIncludeFutureDeliveries={updateIncludeFutureDeliveries}
      updateAutoSplitOrders={updateAutoSplitOrders}
      updateDontExchangePreroute={updateDontExchangePreroute}
      updateConcurrentAllOrNothing={updateConcurrentAllOrNothing}
      relaxedRules={relaxedRules}
      selectedRelaxedRules={selectedRelaxedRules}
      setSelectedRelaxedRules={setSelectedRelaxedRules}
      setPriorityLargeTrucks={setPriorityLargeTrucks}
      priorityLargeTrucks={priorityLargeTrucks}
      updateExpandWorkingStartMinutes={updateExpandWorkingStartMinutes}
      updateExpandWorkingEndMinutes={updateExpandWorkingEndMinutes}
      updateExpandLoadStartMinutes={updateExpandLoadStartMinutes}
      updateExpandLoadEndMinutes={updateExpandLoadEndMinutes}
      updateExpandUnloadStartMinutes={updateExpandUnloadStartMinutes}
      updateExpandUnloadEndMinutes={updateExpandUnloadEndMinutes}
      updateExpandMaxVolumeRate={updateExpandMaxVolumeRate}
      updateExpandMaxLoadCapacityRate={updateExpandMaxLoadCapacityRate}
      updateActivateIncrementalMaxNumPistons={updateActivateIncrementalMaxNumPistons}
      updateSenario={updateSenario}
      updateDisableGlobalOpt={updateDisableGlobalOpt}
      updateTraceCompanyId={updateTraceCompanyId}
    />
  ), [transferRequest, updateBalancedLoading, updateExcludeToll, transferDialogOnClose, transfer, startOn, updateIncludeFutureDeliveries, updateAutoSplitOrders, relaxedRules, selectedRelaxedRules, setSelectedRelaxedRules, setPriorityLargeTrucks, priorityLargeTrucks, updateExpandLoadStartMinutes, updateExpandLoadEndMinutes, updateExpandUnloadStartMinutes, updateExpandUnloadEndMinutes, updateExpandMaxVolumeRate, updateExpandMaxLoadCapacityRate, updateDontExchangePreroute, updateConcurrentAllOrNothing, updateExpandWorkingStartMinutes, updateExpandWorkingEndMinutes, updateActivateIncrementalMaxNumPistons, updateSenario, updateDisableGlobalOpt, updateTraceCompanyId]);

  const deliveryIdSelected = useMemo(() => selectedDeliveryIds.length > 0, [selectedDeliveryIds.length]);

  const actionsMemo = useMemo(() => (
    <ActionsPresenter
      startOn={startOn}
      endOn={endOn}
      isLoading={isLoading}
      planningButtonOnClick={planningButtonOnClick}
      selectImportMethodButtonOnClick={customImportButtonOnClick}
      deliveryIdSelected={deliveryIdSelected}
      estimationButtonOnClick={estimationButtonOnClick}
      allocateHistoriesOnClick={allocateHistoriesOnClick}
      allocateHistoriesForCompany={allocateHistoriesForCompany}
      currentHistoryVersion={currentHistoryVersion}
      resetAllocateHistories={resetAllocateHistories}
      setScenarioPlanningDialogIsOpen={setScenarioPlanningDialogIsOpen}
      currentView={currentView}
      updateView={updateView}
      screenName={SCREEN_NAMES.PLANNING}
    />
  ), [startOn, endOn, isLoading, deliveryIdSelected, allocateHistoriesForCompany, allocateHistoriesOnClick, currentHistoryVersion, estimationButtonOnClick, planningButtonOnClick, customImportButtonOnClick, resetAllocateHistories, setScenarioPlanningDialogIsOpen, currentView, updateView]);

  const displayKindOptions = useMemo(() => ['積地', '納品地', '車庫'], []);
  const storedDisplayKindOptions = useMemo(() => localStorage.getItem('mapDisplayFilter'), []);
  const [displayKinds, setDisplayKinds] = useState<string[]>(storedDisplayKindOptions ? storedDisplayKindOptions.split(',') : displayKindOptions);

  useEffect(() => {
    localStorage.setItem('mapDisplayFilter', displayKinds.join(','));
  }, [displayKinds]);

  const actionsOnMapMemo = useMemo(() => {
    if (currentView !== 'map') return null;

    return (
      <Box
        sx={{
          position: 'absolute',
          left: deliveryDrawerWidth,
          p: 1,
          zIndex: 999,
          width: actionContainerWidth,
        }}
      >
        <ActionsOnMapPresenter
          setShowTruckStatus={setShowTruckStatus}
          displayKindOptions={displayKindOptions}
          displayKinds={displayKinds}
          setDisplayKinds={setDisplayKinds}
        />
      </Box>
    );
  }, [currentView, actionContainerWidth, deliveryDrawerWidth, displayKindOptions, displayKinds]);

  const deliveryElementsRef = useRef<{ [key: number]: HTMLElement }>({});

  const addDeliveryElementRef = useCallback((deliveryId: number, element: HTMLElement) => {
    deliveryElementsRef.current[deliveryId] = element;
  }, []);

  const scrollToDeliveryElement = useCallback((deliveryId: number) => {
    if (deliveryElementsRef.current[deliveryId]) {
      const elem = deliveryElementsRef.current[deliveryId];
      elem.scrollIntoView(true);
    }
  }, []);

  const [confirmDialogIsOpen, setConfirmDialogIsOpen] = useState<boolean>(false);
  const [confirmDialogProps, setConfirmDialogProps] = useState<{
    title: string;
    message: string | React.ReactElement;
    handleOk:() => void;
  }>({
    title: undefined,
    message: '',
    handleOk: () => true,
  });

  const onConfirmDeliveries = useCallback((deliveryIds: number[]) => {
    ReactGA.event('click', { screen_name: SCREEN_NAMES.PLANNING, button_name: '配車確定' });
    const continuousShiftGroupIds = arrayUtil.uniq(deliveryEntities.filter((it) => deliveryIds.includes(it.id)).filter((it) => it.continuousShiftGroupId).map((it) => it.continuousShiftGroupId));
    if (continuousShiftGroupIds.length > 0) {
      setConfirmDialogProps({
        title: '配車確定',
        message: (
          <>
            <Typography>帰庫しない連続勤務は連続する勤務のすべてを確定します。よろしいですか?</Typography>
            {continuousShiftGroupIds.map((id) => {
              const delivery = deliveryEntities.find((it) => it.continuousShiftGroupId === id);
              const driver = driverEntities.find((it) => it.id === delivery.driverId);
              const truck = truckEntities.find((it) => it.id === delivery.truckId);
              return (
                <Stack direction="row" spacing={1} sx={{ mt: 1 }} key={`continuousShiftGroupIds-${id}`}>
                  <Typography>
                    <span style={{ color: unsetColorFor(truck?.temporary ? TRUCK_UNSET_LABEL : truck?.licensePlateValue) }}>{truck?.temporary ? TRUCK_UNSET_LABEL : truck?.licensePlateValue}</span>
                    ／
                    <span style={{ color: unsetColorFor(driver?.temporary ? 'ドライバー未設定' : driver?.name) }}>{driver?.temporary ? 'ドライバー未設定' : driver?.name}</span>
                  </Typography>
                  <Typography>
                    {[datetimeDecorator.toYyyyMmDdHhMm(new Date(delivery.continuousShiftStartAt), '/'), datetimeDecorator.toYyyyMmDdHhMm(new Date(delivery.continuousShiftEndAt), '/')].join('〜')}
                  </Typography>
                </Stack>
              );
            })}
          </>),
        handleOk: () => {
          handleConfirmDeliveries(deliveryIds);
          setConfirmDialogIsOpen(false);
        },
      });
      setConfirmDialogIsOpen(true);
      return;
    }
    handleConfirmDeliveries(deliveryIds);
  }, [deliveryEntities, driverEntities, handleConfirmDeliveries, truckEntities]);

  const onUnconfirmDeliveries = useCallback((deliveryIds: number[]) => {
    ReactGA.event('click', { screen_name: SCREEN_NAMES.PLANNING, button_name: '配車確定解除' });
    const continuousShiftGroupIds = arrayUtil.uniq(deliveryEntities.filter((it) => deliveryIds.includes(it.id)).filter((it) => it.continuousShiftGroupId).map((it) => it.continuousShiftGroupId));
    if (continuousShiftGroupIds.length > 0) {
      setConfirmDialogProps({
        title: '配車確定解除',
        message: (
          <>
            <Typography>帰庫しない連続勤務は連続する勤務のすべてを解除します。よろしいですか?</Typography>
            {continuousShiftGroupIds.map((id) => {
              const delivery = deliveryEntities.find((it) => it.continuousShiftGroupId === id);
              const driver = driverEntities.find((it) => it.id === delivery.driverId);
              const truck = truckEntities.find((it) => it.id === delivery.truckId);
              return (
                <Stack direction="row" key={`continuousShiftGroupIds-${id}`}>
                  <Typography>
                    <span style={{ color: unsetColorFor(truck?.temporary ? TRUCK_UNSET_LABEL : truck?.licensePlateValue) }}>{truck?.temporary ? TRUCK_UNSET_LABEL : truck?.licensePlateValue}</span>
                    ／
                    <span style={{ color: unsetColorFor(driver?.temporary ? 'ドライバー未設定' : driver?.name) }}>{driver?.temporary ? 'ドライバー未設定' : driver?.name}</span>
                  </Typography>
                  <Typography>
                    {[datetimeDecorator.toYyyyMmDdHhMm(new Date(delivery.continuousShiftStartAt), '/'), datetimeDecorator.toYyyyMmDdHhMm(new Date(delivery.continuousShiftEndAt), '/')].join('〜')}
                  </Typography>
                </Stack>
              );
            })}
          </>),
        handleOk: () => {
          handleUnconfirmDeliveries(deliveryIds);
          setConfirmDialogIsOpen(false);
        },
      });
      setConfirmDialogIsOpen(true);
      return;
    }
    handleUnconfirmDeliveries(deliveryIds);
  }, [deliveryEntities, driverEntities, handleUnconfirmDeliveries, truckEntities]);

  const planningTrucksPresenterMemo = useMemo(() => (
    <div>
      <Box
        sx={{
          width: deliveryDrawerWidth,
          position: 'absolute',
          left: 0,
          top: actionBarHeight,
          zIndex: 999,
          height: contentHeight,
          bgcolor: theme.colors.alpha.white[100],
          overflow: 'hidden',
        }}
      >
        <PlanningTrucksPresenter
          startOn={startOn}
          endOn={endOn}
          isLoading={isLoading}
          garageData={garageData}
          truckEntities={truckEntities}
          driverEntities={driverEntities}
          deliveryEntities={deliveryEntities}
          operationEntities={operationEntities}
          openTransferDialog={openTransferDialog}
          updateDisplayOrderId={updateDisplayOrderId}
          mutateDeleteOrdersOperations={mutateDeleteOrdersOperations}
          mutateCloneLastWeekShifts={mutateCloneLastWeekShifts}
          mutateDeleteShiftsOperations={mutateDeleteShiftsOperations}
          swapRequest={swapRequest}
          groupEntities={groupEntities}
          planningsOperationEntitiesWithStatsByDeliveryIdEntity={planningsOperationEntitiesWithStatsByDeliveryIdEntity}
          sendOperationMail={sendOperationMail}
          refreshOperationStatuses={refreshOperationStatuses}
          lastOperationStatusUpdated={lastOperationStatusUpdated}
          resetDrivers={resetDrivers}
          currentHistoryVersion={currentHistoryVersion}
          firstVisitSet={firstVisitSet}
          latestAllocateHistory={latestAllocateHistory}
          selectPrintButtonOnClick={reportPrinting.open}
          requestSingleAlgorithmPlanning={requestSingleAlgorithmPlanning}
          selectedCycleIndexes={selectedCycleIndexes}
          updateSelectedCycleIndexes={updateSelectedCycleIndexes}
          planningsOperationDeliveryByDeliveryIdEntity={planningsOperationDeliveryByDeliveryIdEntity}
          resetEditPlaces={resetEditPlaces}
          updateEditPlaces={updateEditPlaces}
          addEmptyCycle={addEmptyCycle}
          removeEmptyCycle={removeEmptyCycle}
          addDeliveryElementRef={addDeliveryElementRef}
          assignTruckToTemporaryDeliveryRequest={assignTruckToTemporaryDeliveryRequest}
          assignTruckQueue={assignTruckQueue}
          currentAssigning={currentAssigning}
          allDeliveriesLoaded={allDeliveriesLoaded}
          deleteTemporaryDelivery={deleteTemporaryDelivery}
          onConfirmDeliveries={onConfirmDeliveries}
          onUnconfirmDeliveries={onUnconfirmDeliveries}
          isConfirming={isConfirming}
          isUnconfirming={isUnconfirming}
          billingTotal={billingTotal}
          paymentTotal={paymentTotal}
          setTariffCalculationDialogIsOpen={setTariffCalculationDialogIsOpen}
        />
      </Box>
      <Box
        sx={{
          position: 'absolute',
          bottom: '2%',
          left: deliveryDrawerWidth,
          zIndex: 1000,
        }}
      >
        <IconButton
          sx={{
            bgcolor: theme.colors.alpha.white[100],
            borderRadius: '0 5px 5px 0',
            border: '1px solid #ccc',
            px: 0
          }}
          onClick={() => {
            if (deliveryDrawerIsOpen) {
              closeDeliveryDrawer();
            } else {
              openDeliveryDrawer();
            }
          }}
        >
          {
            deliveryDrawerIsOpen
              ? <ChevronLeftRoundedIcon />
              : <ChevronRightRoundedIcon />
          }
        </IconButton>
      </Box>
    </div>
  ), [deliveryDrawerWidth, contentHeight, theme.colors.alpha.white, startOn, endOn, isLoading, garageData, truckEntities, driverEntities, deliveryEntities, operationEntities, openTransferDialog, updateDisplayOrderId, mutateDeleteOrdersOperations, mutateCloneLastWeekShifts, mutateDeleteShiftsOperations, swapRequest, groupEntities, planningsOperationEntitiesWithStatsByDeliveryIdEntity, sendOperationMail, refreshOperationStatuses, lastOperationStatusUpdated, resetDrivers, currentHistoryVersion, firstVisitSet, latestAllocateHistory, reportPrinting.open, requestSingleAlgorithmPlanning, selectedCycleIndexes, updateSelectedCycleIndexes, planningsOperationDeliveryByDeliveryIdEntity, resetEditPlaces, updateEditPlaces, addEmptyCycle, removeEmptyCycle, addDeliveryElementRef, deliveryDrawerIsOpen, closeDeliveryDrawer, openDeliveryDrawer, assignTruckToTemporaryDeliveryRequest, assignTruckQueue, currentAssigning, allDeliveriesLoaded, deleteTemporaryDelivery, isConfirming, isUnconfirming, setTariffCalculationDialogIsOpen]);

  const mapFunctionMemo = useMemo(() => (
    <MapFunctionPresenter
      startOn={startOn}
      endOn={endOn}
      isLoading={isLoading}
      planningMapUnallocatedOrderPositionEntities={planningMapUnallocatedOrderPositionEntities}
      planningsMapOperationPositionEntities={planningsMapOperationPositionEntities}
      planningsMapGaragePositionEntities={planningsMapGaragePositionEntities}
      flyToPosition={flyToPosition}
      resetFlyToPosition={resetFlyToPosition}
      fitBoundsPositions={fitBoundsPositions}
      resetFitBoundsPositions={resetFitBoundsPositions}
    />
  ), [startOn, endOn, isLoading, planningMapUnallocatedOrderPositionEntities, planningsMapOperationPositionEntities, planningsMapGaragePositionEntities, flyToPosition, resetFlyToPosition, fitBoundsPositions, resetFitBoundsPositions]);
  const tileLayerMemo = useMemo(() => (
    <TileLayer
      attribution='&copy; <a href="http://osm.org/copyright">OpenStreetMap</a>'
      url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
    />
  ), []);

  const planningMapDeliveriesPolylinesPresenterMemo = useMemo(() => (
    <PlanningMapDeliveriesPolylinesPresenter
      editPlaces={editPlaces}
      deliveries={deliveryEntities.filter((it) => selectedDeliveryIds.includes(it.id))}
      selectedCycleIndexes={selectedCycleIndexes}
      planningsOperationDeliveryByDeliveryIdEntity={planningsOperationDeliveryByDeliveryIdEntity}
      deliveryEntities={deliveryEntities}
    />
  ), [editPlaces, deliveryEntities, selectedCycleIndexes, planningsOperationDeliveryByDeliveryIdEntity, selectedDeliveryIds]);

  const orderPolylinesPresenterMemo = useMemo(() => {
    if (!shipmentOrderLegRunEntityMap) return null;

    return (
      <OrderPolylinesPresenter selectedIds={selectedOrderIds} shipmentOrderLegRunEntityMap={shipmentOrderLegRunEntityMap} />
    );
  }, [selectedOrderIds, shipmentOrderLegRunEntityMap]);

  const planningMapOrderMarkersPresenterMemo = useMemo(() => (
    <PlanningMapOrderMarkersPresenter
      entities={planningMapUnallocatedOrderPositionEntities}
      selectedIds={selectedOrderIds}
      addSelectedId={addSelectedOrderId}
      removeSelectedId={removeSelectedOrderId}
      shipmentOrderLegRunEntityMap={shipmentOrderLegRunEntityMap}
      displayKinds={displayKinds}
    />
  ), [planningMapUnallocatedOrderPositionEntities, selectedOrderIds, addSelectedOrderId, removeSelectedOrderId, shipmentOrderLegRunEntityMap, displayKinds]);

  const planningMapOperationMarkersPresenterMemo = useMemo(() => (
    <PlanningMapOperationMarkersPresenter
      entities={planningsMapOperationPositionEntities}
      startOn={startOn}
      endOn={endOn}
      updateDisplayOrderId={updateDisplayOrderId}
      mutateDeleteOrdersOperations={mutateDeleteOrdersOperations}
      openTransferDialog={openTransferDialog}
      isLoading={isLoading}
      deliveryEntities={deliveryEntities}
      driverEntities={driverEntities}
      truckEntities={truckEntities}
      garageData={garageData}
      selectedCycleIndexes={selectedCycleIndexes}
      updateSelectedCycleIndexes={updateSelectedCycleIndexes}
      planningsOperationDeliveryByDeliveryIdEntity={planningsOperationDeliveryByDeliveryIdEntity}
      editPlaces={editPlaces}
      scrollToDeliveryElement={scrollToDeliveryElement}
    />
  ), [planningsMapOperationPositionEntities, startOn, endOn, updateDisplayOrderId, mutateDeleteOrdersOperations, openTransferDialog, isLoading, deliveryEntities, driverEntities, truckEntities, garageData, selectedCycleIndexes, updateSelectedCycleIndexes, planningsOperationDeliveryByDeliveryIdEntity, editPlaces, scrollToDeliveryElement]);

  const planningMapGarageMarkersPresenterMemo = useMemo(() => (
    <PlanningMapGarageMarkersPresenter
      entities={planningsMapGaragePositionEntities}
    />
  ), [planningsMapGaragePositionEntities]);

  const planningOrderPresenterMemo = useMemo(() => (
    <PlanningOrderPresenter
      orderId={displayOrderId}
      customInputFields={customInputFields}
    />
  ), [displayOrderId, customInputFields]);

  const backdropMemo = useMemo(() => (
    <Backdrop
      sx={{ color: '#fff', zIndex: theme.zIndex.drawer + 1 }}
      open={calculating}
    >
      <CircularProgress color="inherit" />
    </Backdrop>
  ), [calculating, theme.zIndex.drawer]);

  useEffect(() => {
    const updateWidth = (): void => {
      setWindowWidth(window.innerWidth);
    };

    window.addEventListener('resize', updateWidth);
    updateWidth();

    return () => window.removeEventListener('resize', updateWidth);
  }, []);

  useEffect(() => {
    setAllocatedHistoriesDialogIsOpen(!!currentHistoryVersion);
  }, [currentHistoryVersion]);

  const [timeoutIds, setTimeoutIds] = useState<NodeJS.Timeout[]>([]);
  const [runningSnackbarIsOpen, setRunningSnackbarIsOpen] = useState(false);
  const [runningSnackbarMessage, setRunningSnackbarMessage] = useState('');
  const handleRunningSnackbarClose = useCallback(() => {
    setRunningSnackbarIsOpen(false);
  }, []);

  const stopRunning = useCallback(() => {
    if (confirm('実行中の自動配車を停止します。よろしいですか？')) {
      const entity = planningRunningEntities[0];
      if (entity && entity.id) {
        cancelRunningPlan(entity.id);
      }
      handleRunningSnackbarClose();
    }
  }, [cancelRunningPlan, handleRunningSnackbarClose, planningRunningEntities]);

  const runningSnackbarAction = useMemo(() => (
    <>
      <Button size="small" color="secondary" onClick={stopRunning}>
        実行を停止する
      </Button>
      <Button size="small" color="secondary" onClick={handleRunningSnackbarClose}>
        閉じる
      </Button>
    </>
  ), [handleRunningSnackbarClose, stopRunning]);

  const [suggestionDialogOpen, setSuggestionDialogOpen] = useState(false);
  const handleSuggestionDialogClose = useCallback((event: React.MouseEvent<HTMLElement>) => {
    event.stopPropagation();

    setSuggestionDialogOpen(false);
  }, []);

  const setupRunningSnackbarMessage = useCallback(() => {
    if (!planningRunningEntities) return;

    const startAt = new Date(planningRunningEntities[0].createdAt);
    const diffInMinutes = Math.floor((new Date().getTime() - startAt.getTime()) / (1000 * 60));
    const estimate = asyncPlanningResponse?.estimate ?? '';
    let estimateText = '';
    if (estimate) {
      estimateText = `(${datetimeDecorator.toHourMinutes(new Date(estimate))}頃終了予定)`;
    }
    setRunningSnackbarMessage(`${diffInMinutes < 0 ? 0 : diffInMinutes} 分前から自動配車を実行しています。${estimateText}`);

    const tid = setTimeout(() => {
      setupRunningSnackbarMessage();
      refreshRunningEntities();
    }, 1000 * 60);
    setTimeoutIds((prev) => [...prev, tid]);
  }, [planningRunningEntities, refreshRunningEntities, asyncPlanningResponse]);

  useEffect(() => {
    if (!planningRunningEntities) return;

    if (planningRunningEntities.length > 0) {
      setRunningSnackbarIsOpen(true);
      setupRunningSnackbarMessage();
    } else {
      setRunningSnackbarIsOpen(false);
      setRunningSnackbarMessage('');
    }
  }, [planningRunningEntities, setupRunningSnackbarMessage]);

  useEffect(() => {
    // 画面の軽花粉数表示のため再帰的にsetTimeoutをしている。
    // 配車が終わったらクリアしておかないと、再帰処理が続いてしまい、いつまでもカウントアップしてしまうため、
    // isLoading が false になったらタイマーをクリアする。
    if (!isLoading && timeoutIds.length > 0) {
      timeoutIds.forEach((it) => clearTimeout(it));
      setTimeoutIds([]);
    }
  }, [isLoading, timeoutIds]);

  const mapView = useMemo(() => {
    if (currentView !== 'map') return null;

    return (
      <MapContainer
        style={{
          width: '100vw',
          height: contentHeight
        }}
        center={[35.232811, 139.729094]}
        zoom={8}
        scrollWheelZoom
        zoomControl={false}
      >
        {mapFunctionMemo}
        {tileLayerMemo}
        {displayKinds.includes('車庫') && planningMapGarageMarkersPresenterMemo}
        {planningMapDeliveriesPolylinesPresenterMemo}
        {orderPolylinesPresenterMemo}
        {planningMapOrderMarkersPresenterMemo}
        {planningMapOperationMarkersPresenterMemo}
        {licenseContext.config?.use_truck_position && showTruckStatus && (
          <PlanningMapCurrentPositionsPresenter
            startOn={startOn}
            endOn={endOn}
          />
        )}
      </MapContainer>
    );
  }, [currentView, contentHeight, displayKinds, endOn, licenseContext.config, mapFunctionMemo, orderPolylinesPresenterMemo, planningMapDeliveriesPolylinesPresenterMemo, planningMapGarageMarkersPresenterMemo, planningMapOperationMarkersPresenterMemo, planningMapOrderMarkersPresenterMemo, showTruckStatus, startOn, tileLayerMemo]);

  const allocateHistoriesMemo = useMemo(() => (
    <AllocateHistoryPresenter
      allocateHistoriesForCompany={allocateHistoriesForCompany}
      allocateHistoryListItemButtonOnClick={allocateHistoryListItemButtonOnClick}
      allocateHistoryButtonOnClick={allocateHistoryButtonOnClick}
      allocateHistoryCancelButtonOnClick={allocateHistoryCancelButtonOnClick}
      isLoading={isLoading}
      currentHistoryVersion={currentHistoryVersion}
      unallocatedDrawerWidth={unallocatedDrawerWidth}
    />
  ), [allocateHistoriesForCompany, allocateHistoryButtonOnClick, allocateHistoryCancelButtonOnClick, allocateHistoryListItemButtonOnClick, currentHistoryVersion, isLoading, unallocatedDrawerWidth]);

  const rightSideBarMemo = useMemo(() => {
    if (currentView !== 'map' && !allocatedHistoriesDialogIsOpen) return null;

    return (
      <div>
        <Box
          sx={{
            position: 'absolute',
            bottom: '2%',
            right: unallocatedDrawerWidth,
            zIndex: 1000,
          }}
        >
          <IconButton
            sx={{
              bgcolor: theme.colors.alpha.white[100],
              borderRadius: '5px 0 0 5px',
              border: '1px solid #ccc',
              px: 0
            }}
            onClick={() => {
              if (unallocatedDrawerIsOpen) {
                closeUnallocatedDrawer();
              } else {
                openUnallocatedDrawer();
              }
            }}
          >
            {
              unallocatedDrawerIsOpen
                ? <ChevronRightRoundedIcon />
                : <ChevronLeftRoundedIcon />
            }
          </IconButton>
        </Box>
        <Box
          sx={{
            width: unallocatedDrawerWidth,
            bgcolor: theme.colors.alpha.white[100],
            position: 'absolute',
            right: 0,
            top: actionBarHeight,
            zIndex: 999,
            overflow: 'hidden',
            height: contentHeight,
            borderLeft: '1px solid #ccc',
          }}
        >
          {
            !allocatedHistoriesDialogIsOpen
              ? ordersMemo
              : allocateHistoriesMemo
          }
        </Box>
      </div>
    );
  }, [currentView, allocateHistoriesMemo, allocatedHistoriesDialogIsOpen, closeUnallocatedDrawer, contentHeight, openUnallocatedDrawer, ordersMemo, theme.colors.alpha.white, unallocatedDrawerIsOpen, unallocatedDrawerWidth]);

  const [notAllocDeliveries, setNotAllocDeliveries] = useState<PlanningsDeliveryEntity[]>([]);
  const [notAllocTrucks, setNotAllocTrucks] = useState<PlanningsTruckEntity[]>([]);
  const [notAllocDrivers, setNotAllocDrivers] = useState<PlanningsDriverEntity[]>([]);

  useEffect(() => {
    const notAllocDeliveryIds = plansContext.notAllocReasons?.map((it) => it.delivery_id) || [];
    setNotAllocDeliveries(deliveryEntities.filter((it) => notAllocDeliveryIds.includes(it.id)));
  }, [deliveryEntities, plansContext.notAllocReasons]);

  useEffect(() => {
    const notAllocTruckIds = notAllocDeliveries.map((it) => it.truckId);
    setNotAllocTrucks(truckEntities.filter((it) => notAllocTruckIds.includes(it.id)));
  }, [notAllocDeliveries, truckEntities]);

  useEffect(() => {
    const notAllocDriverIds = notAllocDeliveries.map((it) => it.driverId);
    setNotAllocDrivers(driverEntities.filter((it) => notAllocDriverIds.includes(it.id)));
  }, [driverEntities, notAllocDeliveries]);

  const orderListMemo = useMemo(() => {
    if (currentView !== 'list') return null;

    const deliveryWithTruckAndDriverEntities: PlanningsDeliveryWithTruckAndDriverEntity[] = deliveryEntities.map((delivery) => {
      const truck = truckEntities.find((it) => it.id === delivery.truckId);
      const driver = driverEntities.find((it) => it.id === delivery.driverId);

      return {
        ...delivery,
        truck,
        driver,
      };
    });

    return (
      <Box
        sx={{
          position: 'absolute',
          left: deliveryDrawerWidth,
          top: actionBarHeight,
          height: contentHeight,
          width: `calc(100vw - ${deliveryDrawerWidth}px)`,
          p: 0,
          m: 0,
        }}
      >
        <V2OrdersPresenter
          ref={childRef}
          startDate={startOn}
          endDate={endOn}
          isPlanningView
          setOrderGridApiRef={setOrderGridApiRef}
          notAllocDeliveries={notAllocDeliveries}
          notAllocDrivers={notAllocDrivers}
          notAllocTrucks={notAllocTrucks}
          deliveryWithTruckAndDriverEntities={deliveryWithTruckAndDriverEntities}
          requestForcePlanning={requestForcePlanning}
          resetQuery={resetQuery}
          forcePlanningQueue={forcePlanningQueue}
          currentForcePlanning={currentForcePlanning}
          partnerCompanyEntities={partnerCompanyEntities}
          driverEntities={driverEntities}
          truckEntities={truckEntities}
          afterResetOrders={afterResetOrders}
        />
      </Box>
    );
  }, [currentView, deliveryEntities, truckEntities, driverEntities, deliveryDrawerWidth, contentHeight, startOn, endOn, requestForcePlanning, notAllocDeliveries, notAllocTrucks, notAllocDrivers, forcePlanningQueue, currentForcePlanning, resetQuery, partnerCompanyEntities, afterResetOrders]);

  return (
    <>
      <title>配車計画 | マップ</title>
      <Stack>
        <Stack
          sx={{
            height: actionBarHeight,
            bgcolor: theme.colors.alpha.white[100],
          }}
        >
          {actionsMemo}
        </Stack>
        <Stack
          direction="row"
          sx={{
            height: contentHeight,
            width: '100vw',
            overflow: 'hidden',
          }}
        >
          <Snackbar
            anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
            open={runningSnackbarIsOpen}
            key="RunningSnackbar"
          >
            <SnackbarContent
              message={runningSnackbarMessage}
              action={runningSnackbarAction}
            />
          </Snackbar>
          {mapView}
          {actionsOnMapMemo}
          {planningTrucksPresenterMemo}
          {rightSideBarMemo}
          {orderListMemo}
          {scenarioDialogMemo}
          {tariffCalculationDialogMemo}
          {notAllocSuggestions && (
            <NotAllocSuggestionComponent
              showNotAllocSuggestionSnackbar={showNotAllocSuggestionSnackbar}
              suggestionDialogOpen={suggestionDialogOpen}
              notAllocSuggestions={notAllocSuggestions}
              handleSuggestionDialogClose={handleSuggestionDialogClose}
              setShowNotAllocSuggestionSnackbar={setShowNotAllocSuggestionSnackbar}
              setSuggestionDialogOpen={setSuggestionDialogOpen}
            />
          )}
          <Dialog
            open={planningDialogIsOpen}
            onClose={planningDialogOnClose}
            fullWidth
            maxWidth={false}
            PaperProps={{ sx: { maxWidth: 720 } }}
          >
            {planningMemo}
          </Dialog>
          <Dialog
            open={transferDialogIsOpen}
            onClose={transferDialogOnClose}
            fullWidth
            maxWidth={false}
            PaperProps={{ sx: { maxWidth: 720 } }}
          >
            {transferMemo}
          </Dialog>
          <Dialog
            fullWidth
            maxWidth="md"
            open={displayOrderDialogIsOpen}
            onClose={resetDisplayOrderId}
          >
            {planningOrderPresenterMemo}
          </Dialog>
          <ReportPrintingDialogs {...reportPrinting.dialogProps} />
          <EstimationDialogPresenter
            estimationDialogIsOpen={estimationDialogIsOpen}
            estimationOnClose={estimationOnClose}
            estimationMethod={estimationMethod}
            estimationMethodOnClick={estimationMethodOnClick}
            deliveryEntities={deliveryEntities}
            driverEntities={driverEntities}
            truckEntities={truckEntities}
            selectedDeliveryIds={selectedDeliveryIds}
          />
          {backdropMemo}
        </Stack>
        <ConfirmDialog
          open={confirmDialogIsOpen}
          handleCancel={() => setConfirmDialogIsOpen(false)}
          {...confirmDialogProps}
        />
      </Stack>
    </>
  );
})
);

export default Presenter;
